import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';

import { BaseContainer, DialogConfig, DialogStyle } from '@stylehaven/components';
import { KeyframeItem } from '../../../common/entities/common/keyframe-item.class';
import { OutletCloser } from '../../../common/services/common/outlet-closer.service';

@Component({
    selector: 'med-keyframes-preview',
    templateUrl: './keyframes-preview.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KeyframesPreviewComponent extends BaseContainer implements OnInit, OnDestroy {
    public style: DialogStyle = DialogStyle.ContentOnly;

    public state$: Observable<Record<string, any>>;
    public activeKeyframe$: BehaviorSubject<KeyframeItem>;
    public allKeyframes$: Observable<KeyframeItem[]>;
    public config$: Observable<DialogConfig>;
    public open$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    constructor(
        private readonly outletCloser: OutletCloser,
        private readonly activatedRoute: ActivatedRoute,
        private readonly router: Router
    ) {
        super();
    }

    ngOnInit(): void {
        this.initState();
        this.initAllKeyframesStream();
        this.initActiveKeyframeStream();
        this.initConfigStream();
    }

    private initState() {
        this.state$ = this.activatedRoute.data.pipe(
            take(1),
            tap(state => {
                if (!state.routerState || Object.keys(state.routerState).length === 0) {
                    this.router.navigate(['../'], {
                        relativeTo: this.activatedRoute
                    });
                }
            }),
            filter(state => Object.keys(state.routerState).length > 0),
            map(state => state.routerState)
        );
    }

    private initAllKeyframesStream(): void {
        this.allKeyframes$ = this.state$.pipe(map(data => data.allKeyframes));
    }

    private initActiveKeyframeStream(): void {
        this.state$
            .pipe(map(data => data.currentKeyframe))
            .subscribe(keyframe => (this.activeKeyframe$ = new BehaviorSubject(keyframe)));
    }

    private initConfigStream(): void {
        this.config$ = combineLatest([this.activeKeyframe$, this.allKeyframes$]).pipe(
            map(([activeKeyframe, allKeyframes]) => ({
                data: {
                    fragmentPreviewUrl: activeKeyframe.keyframe.PreviewImagePath,
                    prevAction: this.hasPrev(activeKeyframe)
                        ? () => this.onPrevClicked(activeKeyframe, allKeyframes)
                        : undefined,
                    nextAction: this.hasNext(activeKeyframe, allKeyframes)
                        ? () => this.onNextClicked(activeKeyframe, allKeyframes)
                        : undefined
                }
            }))
        );
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    private hasPrev(keyframe: KeyframeItem): boolean {
        return keyframe.keyframeIndex > 0;
    }

    private hasNext(keyframe: KeyframeItem, allKeyframes: KeyframeItem[]): boolean {
        return keyframe.keyframeIndex < allKeyframes.length - 1;
    }

    public closeModal(): void {
        this.outletCloser.closeOutlet('preview');
    }

    public onPrevClicked(activeKeyframe: KeyframeItem, allKeyframes: KeyframeItem[]): void {
        const prevIndex =
            activeKeyframe.keyframeIndex === 0 ? allKeyframes.length - 1 : activeKeyframe.keyframeIndex - 1;
        this.activeKeyframe$.next(allKeyframes[prevIndex]);
    }

    public onNextClicked(activeKeyframe: KeyframeItem, allKeyframes: KeyframeItem[]): void {
        const nextIndex = (activeKeyframe.keyframeIndex + 1) % allKeyframes.length;
        this.activeKeyframe$.next(allKeyframes[nextIndex]);
    }
}
